(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.StellarBundleCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const validId = value => /^\d+$/.test(String(value)) && Number(value) > 0 && Number.isSafeInteger(Number(value));
  function routes(root) {
    const path = typeof root === 'string' && /^\/(?!\/)[^?#]*\/$/.test(root) ? root : '/';
    return { root: path, cart: path + 'cart', read: path + 'cart.js', add: path + 'cart/add.js', checkout: path + 'checkout' };
  }
  function cleanProperties(properties) {
    const result = Object.create(null);
    for (const [name, value] of Object.entries(properties || {})) {
      if (!name || name.startsWith('_stellar_bundle_')) continue;
      if (typeof value === 'string') { if (value.trim()) result[name] = value; }
      else if (typeof Blob !== 'undefined' && value instanceof Blob && value.size) result[name] = value;
    }
    return result;
  }
  function normalizeItems(items, requestId, offerId) {
    if (!Array.isArray(items) || !items.length || items.length > 24) throw new Error('selection');
    return items.map((item, index) => {
      if (!validId(item.id) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > 999) throw new Error('quantity');
      if (item.selling_plan && !validId(item.selling_plan)) throw new Error('selection');
      return { id: String(item.id), quantity: item.quantity, ...(item.selling_plan ? { selling_plan: String(item.selling_plan) } : {}), properties: { ...cleanProperties(item.properties), _stellar_bundle_request: requestId, _stellar_bundle_line: String(index), _stellar_bundle_id: String(offerId).slice(0, 200) } };
    });
  }
  function bodyFor(items) {
    const body = new FormData();
    items.forEach((item, i) => {
      body.append(`items[${i}][id]`, item.id);
      body.append(`items[${i}][quantity]`, String(item.quantity));
      if (item.selling_plan) body.append(`items[${i}][selling_plan]`, item.selling_plan);
      for (const [key, value] of Object.entries(item.properties)) body.append(`items[${i}][properties][${key}]`, value);
    });
    return body;
  }
  function reconcile(cart, expected) {
    if (!Array.isArray(cart?.items)) return false;
    return expected.every(item => {
      const matches = cart.items.filter(line => String(line.variant_id || line.id) === item.id && line.properties?._stellar_bundle_request === item.properties._stellar_bundle_request && line.properties?._stellar_bundle_line === item.properties._stellar_bundle_line && String(line.selling_plan_allocation?.selling_plan?.id || '') === String(item.selling_plan || ''));
      if (matches.reduce((sum, line) => sum + Number(line.quantity || 0), 0) !== item.quantity) return false;
      return matches.every(line => Object.entries(item.properties).every(([key, value]) => typeof value === 'string' ? line.properties?.[key] === value : typeof line.properties?.[key] === 'string' && line.properties[key].length > 0));
    });
  }
  function checkSelection(variant, quantity, rule, inCart = 0) {
    if (!variant || !variant.available || variant.requires_selling_plan) throw new Error('unavailable');
    const min = Number(rule?.min || 1), increment = Number(rule?.increment || 1), max = rule?.max == null ? null : Number(rule.max);
    if (!Number.isSafeInteger(quantity) || quantity < min || quantity % increment !== 0 || (max !== null && quantity + inCart > max)) throw new Error('quantity');
  }
  function nativeSelection(selected, offer) {
    if (!offer?.available || !validId(offer.id) || !Number.isSafeInteger(offer.price) || offer.price < 0 || !Array.isArray(offer.components) || !offer.components.length || !selected.length) return null;
    const quantities = new Map();
    let regular = 0;
    for (const item of selected) {
      if (!validId(item.id) || !item.available || item.hasOptions || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || !Number.isSafeInteger(item.price) || item.price < 0) return null;
      quantities.set(String(item.id), (quantities.get(String(item.id)) || 0) + item.quantity);
      regular += item.price * item.quantity;
    }
    if (quantities.size !== offer.components.length) return null;
    let sets = Infinity;
    const seen = new Set();
    for (const item of offer.components) {
      const id = String(item.id);
      if (seen.has(id) || !validId(id) || !Number.isSafeInteger(item.quantity) || item.quantity < 1) return null;
      seen.add(id);
      const count = Math.floor(quantities.get(id) / item.quantity);
      if (!Number.isSafeInteger(count) || count < 1 || count > 999) return null;
      sets = Math.min(sets, count);
    }
    const extras = offer.components.map(item => ({ id: String(item.id), quantity: quantities.get(String(item.id)) - item.quantity * sets })).filter(item => item.quantity > 0);
    const prices = new Map(selected.map(item => [String(item.id), item.price]));
    const total = offer.price * sets + extras.reduce((sum, item) => sum + prices.get(item.id) * item.quantity, 0);
    if (total > regular) return null;
    try { checkSelection(offer, sets, offer.rule); } catch (_) { return null; }
    return { id: String(offer.id), quantity: sets, total, saving: regular - total, extras };
  }
  class CartCoordinator {
    constructor(fetcher, routeRoot, tokenFactory) { this.fetch = fetcher; this.urls = routes(routeRoot); this.token = tokenFactory; this.busy = false; this.uncertain = false; this.completed = false; }
    async readCart() {
      const response = await this.fetch(this.urls.read, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('cart_read');
      const cart = await response.json();
      if (!Array.isArray(cart.items) || !cart.currency) throw new Error('cart_read');
      return cart;
    }
    async submit(prepare, offerId) {
      if (this.busy || this.uncertain || this.completed) return { phase: 'not_sent', error: 'busy' };
      this.busy = true;
      let sent = false, expected, response, serverMessage = '';
      try {
        const before = await this.readCart();
        const prepared = await prepare(before);
        expected = normalizeItems(prepared, this.token(), offerId);
        const body = bodyFor(expected);
        sent = true;
        try {
          response = await this.fetch(this.urls.add, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
          const result = await response.json();
          if (result.status) response = { ok: false, status: Number(result.status) };
          serverMessage = typeof result.description === 'string' ? result.description : '';
        } catch (_) { response = null; }
        let cart;
        try { cart = await this.readCart(); } catch (_) { this.uncertain = true; return { phase: 'unknown', error: 'review', sent: true }; }
        const complete = reconcile(cart, expected);
        if (response?.ok && complete) { this.completed = true; return { phase: 'success', cart, sent: true }; }
        this.uncertain = true;
        return { phase: 'review', cart, sent: true, error: 'review', status: response?.status || 0, serverMessage, complete };
      } catch (error) {
        if (sent) this.uncertain = true;
        return { phase: sent ? 'unknown' : 'not_sent', error: sent ? 'review' : error.message, sent };
      } finally { this.busy = false; }
    }
  }
  return { validId, routes, cleanProperties, normalizeItems, bodyFor, reconcile, checkSelection, nativeSelection, CartCoordinator };
});
